import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { FilterByWorkplace } from 'srcReact/components/common/FilterByWorkplace'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import { organizationActions } from 'srcReact/OrganizationPage'
import Avatar from 'srcReact/components/common2/Avatar'
import { urlSearchParams } from 'srcReact/helpers/utils'
import styles from './styles.module.scss'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import Spinner from 'srcReact/components/common2/Spinner'
import OrderByDropdown from 'srcReact/components/common2/OrderByDropdown'
import LightProfileProto from 'srcReact/components/common2/LightProfileModal/LightProfilPrototype'
import { lightProfileActions } from 'srcReact/components/common2/LightProfileModal/actions'

const MemberDirectoryContainer = ({
  current_organization,
  member_list,
  isMobile,
  isTablet,
  isNative,
  loggedInUser,
  current_workplace,
  getMemberDirectory,
  openLightProfileModal,
  t
}) => {
  const { members, loading, stats, no_result } = member_list
  const quantity = 25
  const [workplaceId, setWorkplaceId] = useState('-1')
  const [order, setOrder] = useState('last_joined')
  const [searchValue, setSearchValue] = useState('')

  const [pagination, setPagination] = useState({ nextOffset: 0, isPagination: false })

  const [selectedOrder, setSelectedOrder] = useState({
    value: 'last_joined',
    label: t('orderBy.lastJoined')
  })

  const [orders, setOrders] = useState([
    {
      value: 'last_joined',
      label: t('orderBy.lastJoined')
    },
    { value: 'lastname', label: t('orderBy.name') },
    {
      value: 'shared_interests',
      label: t('orderBy.interests')
    }
  ])

  const observer = useRef()
  const lastMemberRef = useCallback(
    (node) => {
      if (loading) {
        return
      }
      if (observer.current) {
        observer.current.disconnect()
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && stats && stats.remaining_members > 0) {
          setPagination({ nextOffset: stats.next_offset, isPagination: true })
        }
      })
      if (node) {
        observer.current.observe(node)
      }
    },
    [loading]
  )

  const getMembers = () => {
    const organizationId = getCurrentOrgId()
    // nativeapp
    if (isNative) {
      getMemberDirectory(urlSearchParams('organization_id'), workplaceId, searchValue, order, quantity, pagination)
      return
    }

    if (organizationId > 0) {
      getMemberDirectory(organizationId, workplaceId, searchValue, order, quantity, pagination)
    }
  }

  const handleWorkplaceChange = (workplaceId) => {
    setPagination({ nextOffset: 0, isPagination: false })
    setWorkplaceId(workplaceId)
  }

  const handleOrderByChange = (order) => {
    setPagination({ nextOffset: 0, isPagination: false })
    setOrder(order.value)
  }

  const handleSearchMember = (searchValue) => {
    if (searchValue.length !== 1) {
      setPagination({ nextOffset: 0, isPagination: false })
      setSearchValue(searchValue)
    }
  }

  useEffect(() => {
    getMembers()
  }, [order, workplaceId, searchValue, pagination])

  const showUserModal = (user, index) => {
    const lp = new LightProfileProto()
    lp.userId = user.resourceKey
    lp.userIndex = index
    lp.users = members
    openLightProfileModal(lp)
  }

  return (
    <PageContainer
      pageTitle={t('member-directory.title')}
      pageClassName={styles.container}
      withFooter={false}
      withSidebar={true}>
      <div>
        <header className={styles.header}>
          <h1>{t('member-directory.title')}</h1>
        </header>
        <div className={styles.actions}>
          <div className={styles.filters}>
            <SearchTextBox
              placeholder={t('formlabel.searchMemberPlaceholder')}
              handleSearch={handleSearchMember}
              minimumSearchChar={2}
              errorMessage={t('member-directory.search_char_error', { character_count: 1 })}
              cssClass={styles.search}
            />
            <FilterByWorkplace
              allWorkplacesText={t('member-directory.actions.all_workplaces')}
              allWorkplacesInitial={t('member-directory.actions.all_workplaces_initial')}
              handleWorkplaceChange={handleWorkplaceChange}
            />
          </div>
          {!isMobile && (
            <div className={styles.orderby}>
              <OrderByDropdown selected={selectedOrder} options={orders} handleOrderByChange={handleOrderByChange} />
            </div>
          )}
        </div>
        <div className={styles.members}>
          {no_result ? (
            <div className={styles.message}>{t('global.no_results')}</div>
          ) : (
            members &&
            members.map((usr, index) => {
              const isLastMember = members.length === index + 1
              return (
                <Avatar
                  userId={usr.resourceKey}
                  firstname={usr.firstname}
                  lastname={usr.lastname}
                  commonInterestsCount={usr.interests_in_common ? usr.interests_in_common.split(',').length : 0}
                  profilePicture={usr.profile_picture}
                  lastMemberRef={isLastMember ? lastMemberRef : null}
                  key={'member_' + usr.resourceKey}
                  containerCSS={styles.member}
                  userNameCSS={styles.userNameCSS}
                  rounded
                  onClickAction={() => showUserModal(usr, index)}
                  showUserName
                  showCommonInterests
                  mimeType={usr.mime_type}
                  originalTag={usr.original_tag}
                  isOriginal={usr.is_original}
                  imgWrapperSize='large100'
                  withExpertiseGroup={usr.with_expertise_group}
                />
              )
            })
          )}
        </div>

        {loading && <Spinner />}
      </div>
    </PageContainer>
  )
}

const mapStateToProps = (state) => {
  const {
    organization: { current_organization, member_list },
    common: { isMobile, isTablet, isNative },
    login: { user: loggedInUser },
    workplace: { current_workplace }
  } = state

  return { current_organization, member_list, isMobile, isTablet, isNative, loggedInUser, current_workplace }
}

const actionCreators = {
  getMemberDirectory: organizationActions.getMemberDirectory,
  openLightProfileModal: lightProfileActions.openLightProfileModal
}

const ExtendedComponent = withTranslation('common')(MemberDirectoryContainer)

const connectedMemberDirectoryContainer = connect(mapStateToProps, actionCreators)(ExtendedComponent)
export { connectedMemberDirectoryContainer as MemberDirectoryContainer }
